/**
 * Shared input-mask helpers.
 * Slot classes: `0` → digit, `A` → letter, `*` → letter-or-digit; anything else is a literal.
 * Never throws — empty pattern is handled explicitly.
 */

function isSlot(char: string): boolean {
	return char === '0' || char === 'A' || char === '*';
}

function charMatchesSlot(char: string, slot: string): boolean {
	if (slot === '0') return /\d/.test(char);
	if (slot === 'A') return /[a-zA-Z]/.test(char);
	if (slot === '*') return /[a-zA-Z0-9]/.test(char);
	return false;
}

/**
 * Strict exact-length mask check (mirrors C# IsMatch).
 * Empty pattern is not a match.
 */
export function matchesMask(value: string, pattern: string): boolean {
	if (!pattern) return false;
	if (value.length !== pattern.length) return false;

	for (let i = 0; i < pattern.length; i++) {
		const slot = pattern[i];
		const char = value[i];
		if (isSlot(slot)) {
			if (!charMatchesSlot(char, slot)) return false;
		} else if (char !== slot) {
			return false;
		}
	}

	return true;
}

/**
 * Lenient per-keystroke formatter. Characters that cannot fill the next slot are dropped;
 * literals are inserted automatically once preceding slots are filled.
 * When isDeleting, trailing literals are trimmed so backspace is not trapped on them.
 * Empty pattern returns raw unchanged.
 */
export function formatToMask(raw: string, pattern: string, isDeleting = false): string {
	if (!pattern) return raw;

	const slots: string[] = [];
	for (const c of pattern) {
		if (isSlot(c)) slots.push(c);
	}

	// Step 1: keep only characters that satisfy the next unfilled slot
	const kept: string[] = [];
	let slotIdx = 0;
	for (const char of raw) {
		if (slotIdx >= slots.length) break;
		if (charMatchesSlot(char, slots[slotIdx])) {
			kept.push(char);
			slotIdx++;
		}
	}

	// Step 2: re-emit through the pattern, inserting literals when preceding slots are filled
	let result = '';
	let keptIdx = 0;
	for (let i = 0; i < pattern.length; i++) {
		const p = pattern[i];
		if (isSlot(p)) {
			if (keptIdx >= kept.length) break;
			result += kept[keptIdx++];
		} else {
			let precedingSlots = 0;
			for (let j = 0; j < i; j++) {
				if (isSlot(pattern[j])) precedingSlots++;
			}
			if (keptIdx >= precedingSlots) {
				result += p;
			} else {
				break;
			}
		}
	}

	// Step 3: on delete, trim trailing literals so backspace is not stuck on them
	if (isDeleting) {
		while (result.length > 0) {
			const lastPatternChar = pattern[result.length - 1];
			if (isSlot(lastPatternChar)) break;
			result = result.slice(0, -1);
		}
	}

	return result;
}
